import { format, parse, parseISO } from 'date-fns'
/**
 * Format LocalDateTime ISO string (ex: 2025-09-16T22:55:03)
 * => "22:55:03 16/09/2025"
 */
export const formatLocalDateTime = (dateStr: string | undefined): string => {
  if (!dateStr) return ''
  try {
    const date = parseISO(dateStr)
    return format(date, 'HH:mm:ss dd/MM/yyyy')
  } catch (e) {
    console.error('Invalid date format:', dateStr)
    return dateStr
  }
}

export const toBackendDate = (date: Date | null | undefined): string | null => {
  return date ? format(date, 'yyyy-MM-dd') : null
}

export const toBackendDatetime = (date: Date | null | undefined): string | null => {
  return date ? format(date, "yyyy-MM-dd'T'HH:mm:ss") : null
}

export const formatDate = (date: any): string => {
  if (!date) return ''
  if (Array.isArray(date)) {
    return date.map((d) => format(d, 'dd/MM/yyyy')).join(' - ')
  }
  return format(date, 'dd/MM/yyyy')
}

export const parseDate = (input: string): Date => parse(input, 'dd/MM/yyyy', new Date())

/**
 * Chuyển đối tượng Date hoặc chuỗi ISO có timezone
 * sang định dạng local ISO string (giờ máy, không có Z).
 *
 * Ví dụ:
 *   2025-11-01T17:00:00.000Z (UTC)
 *   => 2025-11-02T00:00:00 (giờ Việt Nam)
 */
export function fixDateToLocalISOString(date: Date | string | null | undefined): string | null {
  if (!date) return null

  const d = new Date(date)
  if (isNaN(d.getTime())) return null // tránh lỗi nếu input không hợp lệ

  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 19) // cắt bỏ phần "Z"
}

/**
 * Nếu chỉ muốn lấy ngày (yyyy-MM-dd)
 */
export function toLocalDateString(date: Date | string | null | undefined): string | null {
  if (!date) return null

  const d = new Date(date)
  if (isNaN(d.getTime())) return null

  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 10)
}

/**
 * Hỗ trợ hiển thị giá trị vào VaDateInput
 * @param dateString với 2025-11-01
 */
export function parseBackendDateToDate(dateString: any): Date | null {
  if (!dateString) return null

  const [year, month, day] = dateString.split('-').map(Number) // Nếu backend trả 2025-11-01 thì dùng
  if (!year || !month || !day) return null

  return new Date(year, month - 1, day) // month 0-based
}

/**
 * Hỗ trợ hiển thị giá trị vào VaDateInput
 * @param dateString với 2025-11-01T00:00:00
 */
export function parseBackendDatetimeToDate(dateString: any): Date | null {
  if (!dateString) return null

  const [year, month, day] = dateString.slice(0, 10).split('-').map(Number) // Dùng cho backend trả 2025-11-01T00:00:00
  if (!year || !month || !day) return null

  return new Date(year, month - 1, day) // month 0-based
}

/**
 * LocalDateTime backend ("2026-08-01T09:30:00") → value cho <input type="datetime-local">
 * ("2026-08-01T09:30"). Cắt thẳng chuỗi, KHÔNG qua new Date() để không bị lệch múi giờ.
 */
export function toDatetimeLocalInput(value: string | null | undefined): string {
  if (!value) return ''
  return value.slice(0, 16)
}

/**
 * Value của <input type="datetime-local"> ("2026-08-01T09:30") → LocalDateTime backend
 * ("2026-08-01T09:30:00"). Rỗng → null (xoá mốc). Input chỉ cho tới phút nên giây luôn là 00.
 */
export function fromDatetimeLocalInput(value: string | null | undefined): string | null {
  if (!value) return null
  return value.length === 16 ? `${value}:00` : value
}

/**
 * LocalDateTime backend → "dd/MM/yyyy HH:mm" để hiển thị trên bảng danh sách.
 */
export function formatLocalDateTimeShort(value: string | null | undefined): string {
  if (!value) return ''
  try {
    return format(parseISO(value), 'dd/MM/yyyy HH:mm')
  } catch (e) {
    return String(value)
  }
}

// Convert string "HH:mm:ss" → Date
export function parseTimeStringToDate(timeString?: string | null): Date | null {
  if (!timeString) return null
  const [hours, minutes, seconds] = timeString.split(':').map(Number)
  const d = new Date()
  d.setHours(hours)
  d.setMinutes(minutes)
  d.setSeconds(seconds || 0)
  d.setMilliseconds(0)
  return d
}

// Khi gửi về BE, convert ngược lại Date → "HH:mm:ss"
export function formatDateTypeToTimeString(date?: Date | null): string | null {
  if (!date) return null
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${hours}:${minutes}:${seconds}`
}

// formatDate → hiển thị dd/MM/yyyy.
export const supportVaDateInputFormatDate = (date: Date) => {
  return format(date, 'dd/MM/yyyy') // hiển thị trên UI
}

// parseDate → parse user nhập dd/MM/yyyy thành Date object.
export const supportVaDateInputParseDate = (dateString: string): Date => {
  const parsed = parse(dateString, 'dd/MM/yyyy', new Date())
  if (isNaN(parsed.getTime())) {
    return new Date() // hoặc new Date('1970-01-01') tạm, để thoả type
  }
  return parsed
}
